/**
 * The workflow canvas: a pannable, zoomable world with node cards and wires,
 * flowing top to bottom — inputs on a card's top edge, outputs along its
 * bottom edge, one dot per port.
 *
 * Pointer rules, so nothing fights:
 *   background drag   pans
 *   wheel             zooms about the cursor
 *   card drag         moves the node
 *   output port drag  starts a wire; release on another card's input to connect
 *   port  +           opens the picker, and wires whatever is picked to that port
 *   click             selects a node or a wire; Delete / Backspace removes it
 *
 * The world transform is one CSS `translate() scale()` on a single element,
 * with an SVG for wires inside it, so cards and wires share coordinates and
 * a port dot is exactly where its wire ends.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { Logo } from '@/shared/components'
import type { AppSpec, NodeSpec, StepRecord, WfEdge, WfNode } from '../types'
import { HEAD_H, NODE_W, ROW_H, inputPort, isSingleOutput, nodeHeight, outputPort, outputsOf, portOffset, wirePath, type Point } from './geometry'
import { NodeIcon } from './icons'

export interface CanvasProps {
  nodes: WfNode[]
  edges: WfEdge[]
  specs: Record<string, NodeSpec>
  /** The app each node belongs to, by node id, for the card's logo. */
  apps: Record<string, AppSpec>
  selected: { kind: 'node' | 'edge'; id: string } | null
  onSelect: (sel: { kind: 'node' | 'edge'; id: string } | null) => void
  onMove: (id: string, position: Point) => void
  onConnect: (source: string, port: string, target: string) => void
  onDelete: (sel: { kind: 'node' | 'edge'; id: string }) => void
  /** The + beside a port: add a node wired from there. */
  onAddFrom: (nodeId: string, port: string) => void
  /** The empty canvas's call to action. */
  onAddFirst: () => void
  /** Last run's step per node, to colour the cards. */
  steps?: Record<string, StepRecord>
  readOnly?: boolean
  /** Changes when the space around the canvas changes (a panel opened or
      closed); the graph is re-fitted into the new room. */
  fitKey?: string
}

interface View {
  x: number
  y: number
  k: number
}

const MIN_K = 0.3
const MAX_K = 2
const FIT_MIN_K = 0.6

export function Canvas({ nodes, edges, specs, apps, selected, onSelect, onMove, onConnect, onDelete, onAddFrom, onAddFirst, steps, readOnly, fitKey }: CanvasProps) {
  const host = useRef<HTMLDivElement>(null)
  const [view, setView] = useState<View>({ x: 80, y: 80, k: 1 })
  const [wire, setWire] = useState<{ source: string; port: string; to: Point } | null>(null)
  const drag = useRef<{ kind: 'pan'; start: Point; origin: View; moved: boolean } | { kind: 'node'; id: string; offset: Point } | null>(null)

  const byId = useMemo(() => Object.fromEntries(nodes.map((n) => [n.id, n])), [nodes])
  const wired = useMemo(() => {
    const set = new Set<string>()
    for (const e of edges) set.add(`${e.source}:${e.source_port}`)
    return set
  }, [edges])

  const toWorld = useCallback(
    (clientX: number, clientY: number): Point => {
      const rect = host.current?.getBoundingClientRect()
      if (!rect) return { x: 0, y: 0 }
      return { x: (clientX - rect.left - view.x) / view.k, y: (clientY - rect.top - view.y) / view.k }
    },
    [view],
  )

  // --- pan & zoom --------------------------------------------------------------------

  function onBackgroundDown(e: ReactPointerEvent) {
    if (e.button !== 0) return
    if ((e.target as HTMLElement).closest('.wf-node, .wf-canvas-tools, .wf-canvas-empty')) return
    drag.current = { kind: 'pan', start: { x: e.clientX, y: e.clientY }, origin: view, moved: false }
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }

  function onPointerMove(e: ReactPointerEvent) {
    const d = drag.current
    if (d?.kind === 'pan') {
      d.moved = d.moved || Math.abs(e.clientX - d.start.x) + Math.abs(e.clientY - d.start.y) > 3
      setView({ ...d.origin, x: d.origin.x + (e.clientX - d.start.x), y: d.origin.y + (e.clientY - d.start.y) })
    } else if (d?.kind === 'node') {
      const p = toWorld(e.clientX, e.clientY)
      onMove(d.id, { x: Math.round(p.x - d.offset.x), y: Math.round(p.y - d.offset.y) })
    } else if (wire) {
      setWire({ ...wire, to: toWorld(e.clientX, e.clientY) })
    }
  }

  function onPointerUp(e: ReactPointerEvent) {
    if (wire) {
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null
      const target = el?.closest<HTMLElement>('[data-input-of]')?.dataset.inputOf ?? el?.closest<HTMLElement>('.wf-node')?.dataset.nodeId
      if (target && target !== wire.source && specs[byId[target]?.type]?.has_input !== false) onConnect(wire.source, wire.port, target)
      setWire(null)
    }
    if (drag.current?.kind === 'pan' && !drag.current.moved) onSelect(null)
    drag.current = null
  }

  function onWheel(e: React.WheelEvent) {
    e.preventDefault()
    const rect = host.current?.getBoundingClientRect()
    if (!rect) return
    const px = e.clientX - rect.left
    const py = e.clientY - rect.top
    const factor = Math.exp(-e.deltaY * 0.0015)
    const k = Math.min(MAX_K, Math.max(MIN_K, view.k * factor))
    const x = px - ((px - view.x) * k) / view.k
    const y = py - ((py - view.y) * k) / view.k
    setView({ x, y, k })
  }

  useEffect(() => {
    const el = host.current
    if (!el) return
    const block = (ev: WheelEvent) => ev.preventDefault()
    el.addEventListener('wheel', block, { passive: false })
    return () => el.removeEventListener('wheel', block)
  }, [])

  // --- nodes ---------------------------------------------------------------------------------

  function onNodeDown(e: ReactPointerEvent, node: WfNode) {
    if (e.button !== 0) return
    if ((e.target as HTMLElement).closest('.wf-port-add, .wf-port-out')) return
    e.stopPropagation()
    onSelect({ kind: 'node', id: node.id })
    if (readOnly) return
    const p = toWorld(e.clientX, e.clientY)
    drag.current = { kind: 'node', id: node.id, offset: { x: p.x - node.position.x, y: p.y - node.position.y } }
    host.current?.setPointerCapture(e.pointerId)
  }

  function onPortDown(e: ReactPointerEvent, node: WfNode, port: string) {
    if (readOnly) return
    e.stopPropagation()
    e.preventDefault()
    setWire({ source: node.id, port, to: toWorld(e.clientX, e.clientY) })
    host.current?.setPointerCapture(e.pointerId)
  }

  // --- keyboard ---------------------------------------------------------------------------

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (readOnly || !selected) return
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target as HTMLElement)?.isContentEditable) return
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault()
        onDelete(selected)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selected, onDelete, readOnly])

  // --- fit ----------------------------------------------------------------------------------

  function fit() {
    const rect = host.current?.getBoundingClientRect()
    if (!rect || !nodes.length) return setView({ x: 80, y: 80, k: 1 })
    const xs = nodes.map((n) => n.position.x)
    const ys = nodes.map((n) => n.position.y)
    const minX = Math.min(...xs) - 40
    const minY = Math.min(...ys) - 40
    const maxX = Math.max(...nodes.map((n) => n.position.x + NODE_W)) + 40
    const maxY = Math.max(...nodes.map((n) => n.position.y + nodeHeight(n, specs[n.type]))) + 80
    const k = Math.min(MAX_K, Math.max(FIT_MIN_K, Math.min(rect.width / (maxX - minX), rect.height / (maxY - minY), 1)))
    const fitsX = (maxX - minX) * k <= rect.width
    const x = fitsX ? (rect.width - (maxX - minX) * k) / 2 - minX * k : 24 - minX * k
    setView({ x, y: (rect.height - (maxY - minY) * k) / 2 - minY * k, k })
  }

  const fitted = useRef(false)
  useEffect(() => {
    if (fitted.current || nodes.length === 0 || !Object.keys(specs).length) return
    fitted.current = true
    fit()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes.length, specs])

  const firstFitKey = useRef(fitKey)
  useEffect(() => {
    if (fitKey === firstFitKey.current) return
    firstFitKey.current = fitKey
    const t = window.setTimeout(fit, 220)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey])

  // Keep a newly added node in view: if it landed off-screen, slide over to it.
  const lastCount = useRef(nodes.length)
  useEffect(() => {
    if (nodes.length <= lastCount.current) {
      lastCount.current = nodes.length
      return
    }
    lastCount.current = nodes.length
    const rect = host.current?.getBoundingClientRect()
    const n = nodes[nodes.length - 1]
    if (!rect || !n) return
    const sx = n.position.x * view.k + view.x
    const sy = n.position.y * view.k + view.y
    const pad = 72
    let { x, y } = view
    if (sx + NODE_W * view.k + pad > rect.width) x -= sx + NODE_W * view.k + pad - rect.width
    if (sx < pad) x += pad - sx
    if (sy + (HEAD_H + ROW_H + 40) * view.k + pad > rect.height) y -= sy + (HEAD_H + ROW_H + 40) * view.k + pad - rect.height
    if (sy < pad) y += pad - sy
    if (x !== view.x || y !== view.y) setView({ ...view, x, y })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes.length])

  const wirePoint = wire ? outputPort(byId[wire.source], specs[byId[wire.source]?.type], wire.port) : null

  return (
    <div
      ref={host}
      className="wf-canvas"
      data-wiring={wire ? 'true' : undefined}
      onPointerDown={onBackgroundDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onWheel={onWheel}
    >
      <div className="wf-grid" style={{ backgroundPosition: `${view.x}px ${view.y}px`, backgroundSize: `${24 * view.k}px ${24 * view.k}px` }} />
      <div className="wf-world" style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})` }}>
        <svg className="wf-wires" aria-hidden="true">
          {edges.map((e) => {
            const s = byId[e.source]
            const t = byId[e.target]
            if (!s || !t) return null
            const a = outputPort(s, specs[s.type], e.source_port)
            const b = inputPort(t)
            const on = selected?.kind === 'edge' && selected.id === e.id
            const ran = steps?.[e.source]?.status === 'success' && steps?.[e.target]
            return (
              <g key={e.id} className="wf-wire" data-on={on || undefined} data-ran={ran ? 'true' : undefined}>
                <path className="wf-wire-hit" d={wirePath(a, b)} onPointerDown={(ev) => { ev.stopPropagation(); onSelect({ kind: 'edge', id: e.id }) }} />
                <path className="wf-wire-line" d={wirePath(a, b)} />
              </g>
            )
          })}
          {wire && wirePoint && <path className="wf-wire-draft" d={wirePath(wirePoint, wire.to)} />}
        </svg>

        {nodes.map((n) => {
          const spec = specs[n.type]
          const outputs = outputsOf(n, spec)
          const single = isSingleOutput(outputs)
          const height = nodeHeight(n, spec)
          const step = steps?.[n.id]
          const on = selected?.kind === 'node' && selected.id === n.id
          const app = apps[n.id]
          const action = app?.actions.find((a) => a.id === String(n.params.action_id ?? ''))
          const subtitle = app ? `${app.name}${action ? ` · ${action.name}` : ''}` : spec?.label ?? n.type
          const missing = (n.type === 'connector.action' || n.type === 'trigger.app') && (!n.params.connection_id || !n.params.action_id)
          const ports = outputs.length ? outputs : []
          return (
            <div
              key={n.id}
              className="wf-node"
              data-node-id={n.id}
              data-group={spec?.group ?? 'data'}
              data-on={on || undefined}
              data-status={step?.status}
              data-disabled={n.disabled || undefined}
              data-missing={missing || undefined}
              style={{ transform: `translate(${n.position.x}px, ${n.position.y}px)`, width: NODE_W, height }}
              onPointerDown={(e) => onNodeDown(e, n)}
            >
              {spec?.has_input !== false && <span className="wf-port wf-port-in" data-input-of={n.id} style={{ left: NODE_W / 2, top: 0 }} />}
              <div className="wf-node-head" style={{ height: HEAD_H }}>
                {app ? (
                  <span className="wf-node-logo"><Logo src={app.logo} name={app.name} id={app.id} small /></span>
                ) : (
                  <span className="wf-node-icon"><NodeIcon name={spec?.icon ?? 'workflow'} /></span>
                )}
                <span className="wf-node-title">
                  <span className="wf-node-name">{n.name}</span>
                  <span className="wf-node-type">{missing ? 'Needs an account and action' : subtitle}</span>
                </span>
                {step && (
                  <span className="wf-node-badge" data-status={step.status}>
                    {step.status === 'error' ? '!' : Object.values(step.output_counts).reduce((a, b) => a + b, 0)}
                  </span>
                )}
              </div>
              {!single && (
                <div className="wf-node-ports" style={{ height: ROW_H }}>
                  {ports.map((port) => (
                    <span key={port} className="wf-port-label" data-port={port} style={{ left: portOffset(ports, port) }}>{port}</span>
                  ))}
                </div>
              )}
              {ports.map((port) => {
                const x = portOffset(ports, port)
                return (
                  <span key={port} className="wf-port-anchor" style={{ left: x, top: height }}>
                    <span className="wf-port wf-port-out" data-port={port} onPointerDown={(e) => onPortDown(e, n, port)} />
                    {!readOnly && (
                      <button
                        type="button"
                        className="wf-port-add"
                        data-wired={wired.has(`${n.id}:${port}`) || undefined}
                        title={single ? 'Add a step after this' : `Add a step on ${port}`}
                        aria-label={single ? 'Add a step after this' : `Add a step on ${port}`}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); onAddFrom(n.id, port) }}
                      >+</button>
                    )}
                  </span>
                )
              })}
            </div>
          )
        })}
      </div>

      <div className="wf-canvas-tools">
        <button type="button" className="btn btn-sm" onClick={() => setView((v) => ({ ...v, k: Math.min(MAX_K, v.k * 1.2) }))} aria-label="Zoom in">+</button>
        <button type="button" className="btn btn-sm" onClick={() => setView((v) => ({ ...v, k: Math.max(MIN_K, v.k / 1.2) }))} aria-label="Zoom out">−</button>
        <button type="button" className="btn btn-sm" onClick={fit}>Fit</button>
        <span className="wf-canvas-zoom mono">{Math.round(view.k * 100)}%</span>
      </div>

      {nodes.length === 0 && !readOnly && (
        <div className="wf-canvas-empty">
          <button type="button" className="wf-canvas-start" onClick={onAddFirst}>
            <span className="wf-canvas-start-plus">+</span>
            <strong>Choose a trigger</strong>
            <span>A webhook, a schedule, an app event, or a call.</span>
          </button>
          <span className="small muted">Then press <strong>+</strong> under any step to add the next one below it.</span>
        </div>
      )}
    </div>
  )
}
